import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoForPath } from "@/seo/seoConfig";

const siteName = "Concept Magik";
const siteUrl = process.env.REACT_APP_BACKEND_URL;

const upsertMetaTag = (attributeName, attributeValue, content) => {
  const selector = `meta[${attributeName}="${attributeValue}"]`;
  const existingTags = document.head.querySelectorAll(selector);
  const primaryTag = existingTags[0] ?? document.createElement("meta");

  primaryTag.setAttribute(attributeName, attributeValue);
  primaryTag.setAttribute("content", content);

  if (!existingTags.length) {
    document.head.appendChild(primaryTag);
  }

  existingTags.forEach((tag, index) => {
    if (index > 0) {
      tag.remove();
    }
  });
};

const upsertLinkTag = (rel, href, extraAttributes = {}) => {
  const selector = `link${Object.entries({ rel, ...extraAttributes })
    .map(([key, value]) => `[${key}="${value}"]`)
    .join("")}`;
  const existingTags = document.head.querySelectorAll(selector);
  const primaryTag = existingTags[0] ?? document.createElement("link");

  primaryTag.setAttribute("rel", rel);
  primaryTag.setAttribute("href", href);
  Object.entries(extraAttributes).forEach(([key, value]) => {
    primaryTag.setAttribute(key, value);
  });

  if (!existingTags.length) {
    document.head.appendChild(primaryTag);
  }

  existingTags.forEach((tag, index) => {
    if (index > 0) {
      tag.remove();
    }
  });
};

export default function SeoManager() {
  const location = useLocation();
  useEffect(() => {
    const seo = getSeoForPath(location.pathname);
    const canonicalUrl = `${siteUrl}${seo.path === "/" ? "" : seo.path}`;
    const fullTitle = `${seo.title} | ${siteName}`;

    document.documentElement.lang = "en-US";
    document.title = fullTitle;

    upsertMetaTag("name", "description", seo.description);
    upsertMetaTag("name", "keywords", seo.keywords);
    upsertMetaTag("name", "author", siteName);
    upsertMetaTag("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    upsertMetaTag("name", "googlebot", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    upsertMetaTag("name", "theme-color", "#0F172A");
    upsertMetaTag("name", "geo.region", "US");
    upsertMetaTag("name", "geo.placename", "United States");
    upsertMetaTag("name", "ICBM", "39.8283, -98.5795");

    upsertMetaTag("property", "og:type", "website");
    upsertMetaTag("property", "og:site_name", siteName);
    upsertMetaTag("property", "og:title", fullTitle);
    upsertMetaTag("property", "og:description", seo.description);
    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag("property", "og:image", seo.image);
    upsertMetaTag("property", "og:image:alt", fullTitle);
    upsertMetaTag("property", "og:locale", "en_US");

    upsertMetaTag("name", "twitter:card", "summary_large_image");
    upsertMetaTag("name", "twitter:title", fullTitle);
    upsertMetaTag("name", "twitter:description", seo.description);
    upsertMetaTag("name", "twitter:image", seo.image);

    upsertLinkTag("canonical", canonicalUrl);
    upsertLinkTag("alternate", canonicalUrl, { hreflang: "en-us" });

    document.head.querySelectorAll("script[data-seo-schema='true']").forEach((script) => script.remove());
    seo.schemas.forEach((schema) => {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute("data-seo-schema", "true");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [location.pathname]);

  return null;
}
